import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OptionRowsSkeleton } from '@/components/core';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { Input } from '@/components/ui-kit/input';
import { useMbmProjectSearch, useMbmProjectsByIds } from '../../hooks/use-submissions';

export interface ProjectPickerProps {
  value: string[];
  onChange: (projectIds: string[]) => void;
  readOnly?: boolean;
}

const SEARCH_DEBOUNCE_MS = 300;

/** Annual self reviews must name the customer projects the employee worked on (legacy: MBM project selection). */
export const ProjectPicker = ({ value, onChange, readOnly }: ProjectPickerProps) => {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setKeyword(input.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [input]);

  const { data: results = [], isLoading: isSearchLoading } = useMbmProjectSearch(keyword);
  const { data: selected = [], isLoading: isSelectedLoading } = useMbmProjectsByIds(value);
  const isLoading = isSearchLoading || isSelectedLoading;

  // Keep the current selection visible even when it is not part of the search results.
  const options = [...selected, ...results.filter((project) => !value.includes(project.ProjectId))];

  const toggle = (projectId: string, checked: boolean) =>
    onChange(checked ? [...value, projectId] : value.filter((id) => id !== projectId));

  return (
    <div className="space-y-2">
      <h2 className="text-sm font-semibold">
        {t('CUSTOMER_PROJECTS')} <span className="text-error">*</span>
      </h2>
      {!readOnly && (
        <Input
          placeholder={t('SEARCH_PROJECTS')}
          value={input}
          onChange={(event) => setInput(event.target.value)}
        />
      )}
      <div className="flex max-h-48 flex-col gap-2 overflow-y-auto rounded-md border p-3">
        {/* Not "no data" (and the saved projects appearing late) while the lists load. */}
        {isLoading && options.length === 0 && <OptionRowsSkeleton rows={3} />}
        {!isLoading && options.length === 0 && (
          <p className="text-xs text-muted-foreground">{t('NO_DATA_FOUND')}</p>
        )}
        {options.map((project) => (
          <label key={project.ProjectId} className="flex items-center gap-2 text-sm">
            <Checkbox
              disabled={readOnly}
              checked={value.includes(project.ProjectId)}
              onCheckedChange={(checked) => toggle(project.ProjectId, checked === true)}
            />
            {[project.CustomerName, project.ProjectName].filter(Boolean).join(' — ')}
          </label>
        ))}
      </div>
    </div>
  );
};
